import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, HandCoins, Users, Clock, TrendingUp, Plus, Wallet, 
  CheckCircle, XCircle, AlertCircle, Calendar, Percent, ChevronRight,
  ArrowRightLeft, CreditCard, History, UserPlus, DollarSign, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const LendingPool = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberLoans, setMemberLoans] = useState([]);
  const [payLaterRequests, setPayLaterRequests] = useState([]);
  const [userLendingSummary, setUserLendingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [showCreateLoan, setShowCreateLoan] = useState(false);
  const [showPayLater, setShowPayLater] = useState(false);
  const [showMakePayment, setShowMakePayment] = useState(false);
  const [showCoverShare, setShowCoverShare] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedPayLater, setSelectedPayLater] = useState(null);
  
  // Form states
  const [loanForm, setLoanForm] = useState({
    borrower_id: '',
    principal_amount: '',
    interest_rate: 0,
    due_date: '',
    reason: '',
    terms: ''
  });
  const [payLaterForm, setPayLaterForm] = useState({
    amount: '',
    due_date: '',
    reason: ''
  });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [coverAmount, setCoverAmount] = useState('');

  useEffect(() => {
    fetchData();
  }, [groupId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupRes, loansRes, payLaterRes, summaryRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}?user_id=${user.id}`),
        axios.get(`${API}/loans/group/${groupId}`),
        axios.get(`${API}/pay-later/group/${groupId}`),
        axios.get(`${API}/user/${user.id}/lending-summary`)
      ]);
      
      setGroup(groupRes.data);
      setMembers(groupRes.data.members || []);
      setMemberLoans(loansRes.data);
      setPayLaterRequests(payLaterRes.data);
      setUserLendingSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLoan = async () => {
    if (!loanForm.borrower_id || !loanForm.principal_amount || !loanForm.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        group_id: groupId,
        lender_id: user.id,
        borrower_id: loanForm.borrower_id,
        principal_amount: loanForm.principal_amount,
        due_date: loanForm.due_date,
        interest_rate: loanForm.interest_rate || 0,
        reason: loanForm.reason || '',
        terms: loanForm.terms || ''
      });
      
      await axios.post(`${API}/loans/member-to-member?${params}`);
      toast.success('Loan created successfully!');
      setShowCreateLoan(false);
      setLoanForm({ borrower_id: '', principal_amount: '', interest_rate: 0, due_date: '', reason: '', terms: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create loan');
    }
  };

  const handleCreatePayLater = async () => {
    if (!payLaterForm.amount || !payLaterForm.due_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        group_id: groupId,
        user_id: user.id,
        amount: payLaterForm.amount,
        due_date: payLaterForm.due_date,
        reason: payLaterForm.reason || ''
      });
      
      await axios.post(`${API}/pay-later/create?${params}`);
      toast.success('Pay-later request created. Group members will be notified!');
      setShowPayLater(false);
      setPayLaterForm({ amount: '', due_date: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    }
  };

  const handleMakePayment = async () => {
    if (!paymentAmount || !selectedLoan) return;
    
    try {
      await axios.post(`${API}/loans/${selectedLoan.id}/pay?user_id=${user.id}&amount=${paymentAmount}&note=${encodeURIComponent(paymentNote)}`);
      toast.success('Payment recorded!');
      setShowMakePayment(false);
      setSelectedLoan(null);
      setPaymentAmount('');
      setPaymentNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make payment');
    }
  };

  const handleCoverShare = async () => {
    if (!coverAmount || !selectedPayLater) return;
    
    try {
      const response = await axios.post(`${API}/pay-later/${selectedPayLater.id}/cover?covering_user_id=${user.id}&amount=${coverAmount}`);
      toast.success(`You covered ₹${coverAmount} for ${selectedPayLater.user_name}. A loan has been auto-created for tracking.`);
      setShowCoverShare(false);
      setSelectedPayLater(null);
      setCoverAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cover share');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-500/20 text-blue-400';
      case 'paid': return 'bg-green-500/20 text-green-400';
      case 'overdue': return 'bg-red-500/20 text-red-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'fully_covered': return 'bg-green-500/20 text-green-400';
      case 'partially_covered': return 'bg-orange-500/20 text-orange-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/group/${groupId}`)} className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Lending Hub</h1>
              <p className="text-emerald-100 text-sm">{group?.name}</p>
            </div>
          </div>
          
          {/* Summary Cards */}
          {userLendingSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <p className="text-emerald-100 text-xs">You've Lent</p>
                <p className="text-xl font-bold">₹{userLendingSummary.total_lent_outstanding?.toLocaleString()}</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <p className="text-emerald-100 text-xs">You Owe</p>
                <p className="text-xl font-bold">₹{userLendingSummary.total_borrowed_outstanding?.toLocaleString()}</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <p className="text-emerald-100 text-xs">Covered for Others</p>
                <p className="text-xl font-bold">₹{userLendingSummary.total_covered_for_others?.toLocaleString()}</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <p className="text-emerald-100 text-xs">Net Position</p>
                <p className={`text-xl font-bold ${userLendingSummary.net_lending_position >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {userLendingSummary.net_lending_position >= 0 ? '+' : ''}₹{userLendingSummary.net_lending_position?.toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="loans">Member Loans</TabsTrigger>
            <TabsTrigger value="pay-later">Pay Later</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCreateLoan(true)}
                className="bg-card rounded-2xl p-5 border border-border text-left hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mb-3">
                  <HandCoins className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold mb-1">Lend to Member</h3>
                <p className="text-xs text-muted-foreground">Help a group member with a loan</p>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowPayLater(true)}
                className="bg-card rounded-2xl p-5 border border-border text-left hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mb-3">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold mb-1">Request Pay Later</h3>
                <p className="text-xs text-muted-foreground">Delay your contribution, let others help</p>
              </motion.button>
            </div>

            {/* Recent Activity */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-2xl border border-border p-5"
            >
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-muted-foreground" />
                Recent Lending Activity
              </h2>
              
              {memberLoans.length === 0 && payLaterRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <HandCoins className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No lending activity yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {[...memberLoans.slice(0, 3), ...payLaterRequests.slice(0, 2)].map((item, index) => (
                    <div key={item.id || index} className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          item.lender_id ? 'bg-blue-500/20 text-blue-500' : 'bg-orange-500/20 text-orange-500'
                        }`}>
                          {item.lender_id ? <ArrowRightLeft className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {item.lender_id 
                              ? `${item.lender_name} → ${item.borrower_name}`
                              : `${item.user_name} requested pay-later`
                            }
                          </p>
                          <p className="text-xs text-muted-foreground">
                            ₹{(item.principal_amount || item.original_amount)?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Member Loans Tab */}
          <TabsContent value="loans" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold">Member-to-Member Loans</h2>
              <Button onClick={() => setShowCreateLoan(true)} className="rounded-full gradient-primary" size="sm">
                <Plus className="w-4 h-4 mr-1" /> New Loan
              </Button>
            </div>

            {memberLoans.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-2xl border border-border">
                <HandCoins className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No loans yet</p>
                <Button onClick={() => setShowCreateLoan(true)} variant="outline" className="mt-4 rounded-full">
                  Create First Loan
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {memberLoans.map((loan) => {
                  const isLender = loan.lender_id === user.id;
                  const isBorrower = loan.borrower_id === user.id;
                  
                  return (
                    <motion.div
                      key={loan.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-card rounded-2xl border border-border p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isLender ? 'bg-green-500/20 text-green-500' : 
                            isBorrower ? 'bg-red-500/20 text-red-500' :
                            'bg-blue-500/20 text-blue-500'
                          }`}>
                            {isLender ? <TrendingUp className="w-6 h-6" /> : <Wallet className="w-6 h-6" />}
                          </div>
                          <div>
                            <p className="font-semibold">
                              {isLender ? `Lent to ${loan.borrower_name}` : 
                               isBorrower ? `Borrowed from ${loan.lender_name}` :
                               `${loan.lender_name} → ${loan.borrower_name}`}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Due: {new Date(loan.due_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(loan.status)}`}>
                          {loan.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div className="bg-muted/30 rounded-xl p-3 text-center">
                          <p className="text-xs text-muted-foreground">Principal</p>
                          <p className="font-bold">₹{loan.principal_amount?.toLocaleString()}</p>
                        </div>
                        <div className="bg-muted/30 rounded-xl p-3 text-center">
                          <p className="text-xs text-muted-foreground">Interest</p>
                          <p className="font-bold">{loan.interest_rate || 0}%</p>
                        </div>
                        <div className="bg-muted/30 rounded-xl p-3 text-center">
                          <p className="text-xs text-muted-foreground">Remaining</p>
                          <p className="font-bold text-red-500">₹{loan.remaining?.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Repayment Progress</span>
                          <span>{Math.round((loan.amount_paid / loan.total_due) * 100)}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all"
                            style={{ width: `${(loan.amount_paid / loan.total_due) * 100}%` }}
                          />
                        </div>
                      </div>

                      {/* Payment History */}
                      {loan.payments && loan.payments.length > 0 && (
                        <details className="mb-3">
                          <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                            View {loan.payments.length} payment(s)
                          </summary>
                          <div className="mt-2 space-y-1">
                            {loan.payments.map((payment, idx) => (
                              <div key={idx} className="text-xs flex justify-between p-2 rounded bg-muted/30">
                                <span>₹{payment.amount} - {payment.note || 'Payment'}</span>
                                <span className="text-muted-foreground">{new Date(payment.date).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}

                      {/* Actions */}
                      {isBorrower && loan.status !== 'paid' && (
                        <Button 
                          onClick={() => { setSelectedLoan(loan); setShowMakePayment(true); }}
                          className="w-full rounded-full"
                          size="sm"
                        >
                          <DollarSign className="w-4 h-4 mr-1" /> Make Payment
                        </Button>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Pay Later Tab */}
          <TabsContent value="pay-later" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold">Pay Later Requests</h2>
              <Button onClick={() => setShowPayLater(true)} className="rounded-full bg-gradient-to-r from-orange-500 to-amber-500" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Request
              </Button>
            </div>

            {payLaterRequests.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-2xl border border-border">
                <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No pay-later requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {payLaterRequests.map((pl) => {
                  const isOwner = pl.user_id === user.id;
                  const canCover = !isOwner && pl.status !== 'fully_covered';
                  
                  return (
                    <motion.div
                      key={pl.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-card rounded-2xl border border-border p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isOwner ? 'bg-orange-500/20 text-orange-500' : 'bg-violet-500/20 text-violet-500'
                          }`}>
                            <Clock className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="font-semibold">
                              {isOwner ? 'Your Pay Later Request' : `${pl.user_name} needs help`}
                            </p>
                            <p className="text-sm text-muted-foreground">Due: {new Date(pl.due_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(pl.status)}`}>
                          {pl.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div className="bg-muted/30 rounded-xl p-3 text-center">
                          <p className="text-xs text-muted-foreground">Amount</p>
                          <p className="font-bold">₹{pl.original_amount?.toLocaleString()}</p>
                        </div>
                        <div className="bg-muted/30 rounded-xl p-3 text-center">
                          <p className="text-xs text-muted-foreground">Covered</p>
                          <p className="font-bold text-green-500">₹{pl.total_covered?.toLocaleString() || 0}</p>
                        </div>
                        <div className="bg-muted/30 rounded-xl p-3 text-center">
                          <p className="text-xs text-muted-foreground">Remaining</p>
                          <p className="font-bold text-red-500">₹{pl.remaining_to_cover?.toLocaleString()}</p>
                        </div>
                      </div>

                      {pl.reason && (
                        <p className="text-sm text-muted-foreground mb-3 italic">"{pl.reason}"</p>
                      )}

                      {/* Who covered */}
                      {pl.covered_by && pl.covered_by.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-muted-foreground mb-1">Covered by:</p>
                          <div className="flex flex-wrap gap-2">
                            {pl.covered_by.map((cover, idx) => (
                              <span key={idx} className="px-2 py-1 rounded-full bg-green-500/10 text-green-600 text-xs">
                                {cover.user_name}: ₹{cover.amount}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {canCover && (
                        <Button 
                          onClick={() => { setSelectedPayLater(pl); setCoverAmount(pl.remaining_to_cover?.toString()); setShowCoverShare(true); }}
                          className="w-full rounded-full bg-gradient-to-r from-violet-500 to-purple-500"
                          size="sm"
                        >
                          <UserPlus className="w-4 h-4 mr-1" /> Cover Their Share
                        </Button>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Loan Dialog */}
      <Dialog open={showCreateLoan} onOpenChange={setShowCreateLoan}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Lend to a Member</DialogTitle>
            <DialogDescription>Create a loan to help a group member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Who are you lending to? *</Label>
              <select
                value={loanForm.borrower_id}
                onChange={(e) => setLoanForm({ ...loanForm, borrower_id: e.target.value })}
                className="w-full mt-1 px-4 py-2 rounded-xl border border-border bg-background"
              >
                <option value="">Select member</option>
                {members.filter(m => m.id !== user.id).map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={loanForm.principal_amount}
                  onChange={(e) => setLoanForm({ ...loanForm, principal_amount: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="1000"
                />
              </div>
              <div>
                <Label>Interest Rate (%)</Label>
                <Input
                  type="number"
                  value={loanForm.interest_rate}
                  onChange={(e) => setLoanForm({ ...loanForm, interest_rate: parseFloat(e.target.value) || 0 })}
                  className="mt-1 rounded-xl"
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={loanForm.due_date}
                onChange={(e) => setLoanForm({ ...loanForm, due_date: e.target.value })}
                className="mt-1 rounded-xl"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={loanForm.reason}
                onChange={(e) => setLoanForm({ ...loanForm, reason: e.target.value })}
                className="mt-1 rounded-xl"
                placeholder="e.g., Emergency expense"
              />
            </div>
            <div>
              <Label>Additional Terms</Label>
              <Textarea
                value={loanForm.terms}
                onChange={(e) => setLoanForm({ ...loanForm, terms: e.target.value })}
                className="mt-1 rounded-xl"
                placeholder="Any special conditions..."
                rows={2}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setShowCreateLoan(false)}>
                Cancel
              </Button>
              <Button className="flex-1 rounded-full gradient-primary" onClick={handleCreateLoan}>
                Create Loan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pay Later Dialog */}
      <Dialog open={showPayLater} onOpenChange={setShowPayLater}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Request Pay Later</DialogTitle>
            <DialogDescription>Let others know you need to delay your contribution</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount You Need Help With (₹) *</Label>
              <Input
                type="number"
                value={payLaterForm.amount}
                onChange={(e) => setPayLaterForm({ ...payLaterForm, amount: e.target.value })}
                className="mt-1 rounded-xl"
                placeholder="500"
              />
            </div>
            <div>
              <Label>When Can You Pay Back? *</Label>
              <Input
                type="date"
                value={payLaterForm.due_date}
                onChange={(e) => setPayLaterForm({ ...payLaterForm, due_date: e.target.value })}
                className="mt-1 rounded-xl"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label>Reason (Optional)</Label>
              <Textarea
                value={payLaterForm.reason}
                onChange={(e) => setPayLaterForm({ ...payLaterForm, reason: e.target.value })}
                className="mt-1 rounded-xl"
                placeholder="Let your group know why..."
                rows={2}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setShowPayLater(false)}>
                Cancel
              </Button>
              <Button className="flex-1 rounded-full bg-gradient-to-r from-orange-500 to-amber-500" onClick={handleCreatePayLater}>
                Request Help
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Make Payment Dialog */}
      <Dialog open={showMakePayment} onOpenChange={setShowMakePayment}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Make a Payment</DialogTitle>
            <DialogDescription>
              {selectedLoan && `Pay back your loan to ${selectedLoan.lender_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedLoan && (
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Remaining:</span>
                  <span className="font-bold text-red-500">₹{selectedLoan.remaining?.toLocaleString()}</span>
                </div>
              </div>
            )}
            <div>
              <Label>Payment Amount (₹)</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="mt-1 rounded-xl"
                placeholder="Enter amount"
                max={selectedLoan?.remaining}
              />
            </div>
            <div>
              <Label>Note (Optional)</Label>
              <Input
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                className="mt-1 rounded-xl"
                placeholder="e.g., Partial payment"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setShowMakePayment(false)}>
                Cancel
              </Button>
              <Button className="flex-1 rounded-full gradient-primary" onClick={handleMakePayment}>
                Pay ₹{paymentAmount || 0}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cover Share Dialog */}
      <Dialog open={showCoverShare} onOpenChange={setShowCoverShare}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Cover Someone's Share</DialogTitle>
            <DialogDescription>
              {selectedPayLater && `Help ${selectedPayLater.user_name} by covering their contribution`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPayLater && (
              <div className="bg-violet-500/10 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground">Remaining to cover:</p>
                <p className="text-2xl font-bold text-violet-600">₹{selectedPayLater.remaining_to_cover?.toLocaleString()}</p>
              </div>
            )}
            <div>
              <Label>How much will you cover? (₹)</Label>
              <Input
                type="number"
                value={coverAmount}
                onChange={(e) => setCoverAmount(e.target.value)}
                className="mt-1 rounded-xl"
                max={selectedPayLater?.remaining_to_cover}
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will be tracked as a loan they owe you
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setShowCoverShare(false)}>
                Cancel
              </Button>
              <Button className="flex-1 rounded-full bg-gradient-to-r from-violet-500 to-purple-500" onClick={handleCoverShare}>
                Cover ₹{coverAmount || 0}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LendingPool;
